import { useEffect, useMemo, useState } from 'react';
import {
  FiMapPin,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUserPlus,
  FiUserMinus,
  FiUserCheck,
  FiUpload,
  FiDownload,
  FiFolder,
  FiUsers,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  ServiceStationForm,
  StationImportModal,
  Tooltip,
  Select,
  MultiSelect
} from '../../components';
import { usePermission } from '../../hooks/usePermission';
import EntityFilesModal from '../../components/EntityFilesModal';
import LinkedEmployeesModal from './LinkedEmployeesModal';
import '../CompanyManager/style.css';

export default function ServiceStationManager() {
  const permissions = usePermission('management');
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser'));
    } catch (e) {
      return null;
    }
  });
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [companies, setCompanies] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [stationToDelete, setStationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [company, setCompany] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedStationForLinking, setSelectedStationForLinking] = useState(null);
  const [linkedEmployees, setLinkedEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [searchLinked, setSearchLinked] = useState('');
  const [searchUnlinked, setSearchUnlinked] = useState('');
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedStationForFiles, setSelectedStationForFiles] = useState(null);
  const [isLinkedEmployeesModalOpen, setIsLinkedEmployeesModalOpen] = useState(false);
  const [selectedStationForLinkedView, setSelectedStationForLinkedView] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const storedCompany = localStorage.getItem('selectedCompany');
        if (storedCompany) {
          setCompany(JSON.parse(storedCompany));
        }

        const companiesData = await Api.getCompanies();
        const companiesList = companiesData || [];
        setCompanies(companiesList);
        
        if (companiesList.length === 0) {
          setLoading(false);
        }
      } catch (e) {
        // Fallback or error handling
        setError('Erro ao carregar dados iniciais.');
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  useEffect(() => {
    if (companies.length > 0) {
      loadStations(companyFilter);
    }
  }, [companyFilter, companies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, companyFilter]);

  async function loadStations(filterValue, companiesList = companies) {
    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        const listToUse = companiesList.length > 0 ? companiesList : companies;
        if (listToUse.length === 0) {
            // Try fetching if empty and we want all?
            // Assuming initial load handles it.
        }

        const promises = listToUse.map(c => 
          Api.getServiceStations(c.id)
            .then(stations => stations.map(s => ({ ...s, company: c })))
            .catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const stations = await Api.getServiceStations(filterValue);
        const listToUse = companiesList.length > 0 ? companiesList : companies;
        const selectedComp = listToUse.find(c => c.id === Number(filterValue));
        data = stations.map(s => ({ ...s, company: selectedComp }));
      }

      if (Array.isArray(data)) {
        setStations(data);
      } else {
        setError('Não foi possível carregar os postos de serviço.');
      }
    } catch (err) {
      setError('Erro ao carregar postos de serviço. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedStation(null);
    setIsModalOpen(true);
  }

  function handleEdit(station) {
    setSelectedStation(station);
    setIsModalOpen(true);
  }

  const handleExport = () => {
    if (!stations || stations.length === 0) {
        toast.warning('Não há dados para exportar.');
        return;
    }

    const exportData = stations.map(s => ({
        'Empresa': s.company ? (s.company.corporate_name || s.company.trade_name || s.company.name || s.company.razao_social || s.company.nome_fantasia || `Empresa ${s.company.id}`) : 'N/A',
        'Nome': s.name,
        'Apelido': s.nickname,
        'CNPJ': s.cnpj,
        'Endereço': s.address_street,
        'Número': s.address_number,
        'Complemento': s.address_complement,
        'Bairro': s.address_district,
        'Cidade': s.address_city,
        'UF': s.address_state,
        'CEP': s.address_zip_code,
        'E-mail Responsável': s.responsible_email,
        'Telefone Responsável': s.responsible_phone,
        'Raio Cerca (km)': s.fence_radius_km,
        'Rotativo': s.rotativo,
        'Status': s.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Postos");
    XLSX.writeFile(wb, "postos_de_servico.xlsx");
  };

  function handleFormSuccess() {
    loadStations(companyFilter);
    setIsModalOpen(false);
    setSelectedStation(null);
  }

  function handleRequestDelete(station) {
    setStationToDelete(station);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!stationToDelete) return;

    const companyId = stationToDelete.company?.id || stationToDelete.company_id;

    try {
      setIsDeleting(true);
      
      // Soft delete: update status to 'inativo'
      const payload = {
        ...stationToDelete,
        status: 'inativo'
      };

      const result = await Api.updateServiceStation(companyId, stationToDelete.id, payload);

      if (result && !result.error) {
        // Update local state
        setStations(prev => prev.map(s => 
            s.id === stationToDelete.id ? { ...s, status: 'inativo' } : s
        ));
        setIsDeleteModalOpen(false);
        setStationToDelete(null);
        toast.success('Posto de serviço inativado com sucesso!');
      } else {
        toast.error(
          result && result.error
            ? result.error
            : 'Erro ao inativar posto de serviço.',
        );
      }
    } catch (err) {
      toast.error('Erro ao inativar posto de serviço.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setStationToDelete(null);
  }

  async function handleLinkEmployees(station) {
    setSelectedStationForLinking(station);
    setIsLinkModalOpen(true);
    setLinkLoading(true);
    setLinkedEmployees([]);
    setAllEmployees([]);
    setSearchLinked('');
    setSearchUnlinked('');

    try {
      const companyId = station.company?.id || station.company_id || (company && company.id);

      if (companyId) {
        const [linked, all] = await Promise.all([
          Api.getServiceStationEmployees(companyId, station.id),
          Api.getEmployees(companyId),
        ]);

        setLinkedEmployees(Array.isArray(linked) ? linked : []);
        setAllEmployees(Array.isArray(all) ? all : []);
      }
    } catch (err) {
      toast.error('Erro ao carregar funcionários.');
    } finally {
      setLinkLoading(false);
    }
  }

  async function handleLink(employeeId) {
    try {
      const companyId = selectedStationForLinking.company?.id || selectedStationForLinking.company_id || (company && company.id);
      
      await Api.addServiceStationEmployee(
        companyId,
        selectedStationForLinking.id,
        employeeId,
      );

      const employee = allEmployees.find(e => e.id === employeeId);
      if (employee) {
        setLinkedEmployees(prev => [...prev, employee]);
        
        // Update local station count
        setStations(prev => prev.map(station => {
          if (station.id === selectedStationForLinking.id) {
            return {
              ...station,
              employeeCount: (station.employeeCount || 0) + 1
            };
          }
          return station;
        }));
      }
      toast.success('Funcionário vinculado!');
    } catch (err) {
      toast.error('Erro ao vincular funcionário.');
    }
  }

  async function handleUnlink(employeeId) {
    try {
      const companyId = selectedStationForLinking.company?.id || selectedStationForLinking.company_id || (company && company.id);

      await Api.removeServiceStationEmployee(
        companyId,
        selectedStationForLinking.id,
        employeeId,
      );

      setLinkedEmployees(prev => prev.filter(e => e.id !== employeeId));
      
      // Update local station count
      setStations(prev => prev.map(station => {
        if (station.id === selectedStationForLinking.id) {
          return {
            ...station,
            employeeCount: Math.max((station.employeeCount || 0) - 1, 0)
          };
        }
        return station;
      }));

      toast.success('Funcionário desvinculado!');
    } catch (err) {
      toast.error('Erro ao desvincular funcionário.');
    }
  }

  const unlinkedEmployees = useMemo(() => {
    const linkedIds = new Set(linkedEmployees.map(e => e.id));
    return allEmployees.filter(e => !linkedIds.has(e.id));
  }, [allEmployees, linkedEmployees]);

  const filteredLinkedEmployees = useMemo(() => {
    if (!searchLinked) return linkedEmployees;
    const term = searchLinked.toLowerCase();
    return linkedEmployees.filter(e =>
      e.name.toLowerCase().includes(term) || (e.cpf && e.cpf.includes(term))
    );
  }, [linkedEmployees, searchLinked]);

  const filteredUnlinkedEmployees = useMemo(() => {
    if (!searchUnlinked) return unlinkedEmployees;
    const term = searchUnlinked.toLowerCase();
    return unlinkedEmployees.filter(e =>
      e.name.toLowerCase().includes(term) || (e.cpf && e.cpf.includes(term))
    );
  }, [unlinkedEmployees, searchUnlinked]);

  const companyOptions = useMemo(() => [
    { label: 'TODAS', value: 'all' },
    ...companies.map(c => ({ 
      label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`, 
      value: c.id 
    }))
  ], [companies]);

  const filteredStations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return stations.filter(station => {
      const matchesSearch =
        !term ||
        (station.name || '').toLowerCase().includes(term) ||
        (station.corporate_name || '').toLowerCase().includes(term) ||
        (station.nickname || '').toLowerCase().includes(term) ||
        (station.cnpj || '').toLowerCase().includes(term);

      let matchesStatus = true;

      if (statusFilter !== 'all') {
        matchesStatus = station.status === statusFilter;
      }

      const companyId = station.company?.id || station.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesStatus && matchesCompany;
    });
  }, [stations, searchTerm, statusFilter, companyFilterValues]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  const totalItems = filteredStations.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedStations = filteredStations.slice(
    startIndex,
    startIndex + pageSize,
  );

  function handleOpenFiles(station) {
    setSelectedStationForFiles(station);
    setIsFilesModalOpen(true);
  }

  function handleOpenLinkedEmployees(station) {
    setSelectedStationForLinkedView(station);
    setIsLinkedEmployeesModalOpen(true);
  }

  const columns = [
    {
      id: 'station',
      header: 'Posto',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: station => (
        <>
          <div className="manager-table-avatar">
            <FiMapPin size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {station.name}
            </span>
            <span className="manager-table-subtitle">
              {station.address_city && station.address_state
                ? `${station.address_city} - ${station.address_state}`
                : `ID: ${station.id}`}
            </span>
            {station.latitude && station.longitude && (
              <span className="manager-table-subtitle" style={{ color: 'var(--success-color)', fontSize: '0.75rem', marginTop: '2px' }}>
                <FiMapPin size={12} style={{ marginRight: '4px' }} />
                Localização inserida
              </span>
            )}
          </div>
        </>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: station => (
        <span className="manager-table-subtitle">
          {station.company ? (station.company.corporate_name || station.company.trade_name || station.company.name || station.company.razao_social || station.company.nome_fantasia || 'Sem nome') : 'N/A'}
        </span>
      ),
    },
    {
      id: 'cnpj',
      header: 'CNPJ',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: station => (
        <div className="manager-table-cnpj">
          <span>{station.cnpj || 'Não informado'}</span>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Tipo',
      width: '1.4fr',
      cellClassName: 'manager-table-cell',
      render: station => (
        <span className="manager-table-subtitle">
          {station.type === 'public' ? 'Público' : 'Privado'}
        </span>
      ),
    },
    {
      id: 'employees',
      header: 'Funcionários',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: station => (
        <div 
          onClick={(e) => {
            e.stopPropagation();
            handleOpenLinkedEmployees(station);
          }}
          style={{ 
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e0f2fe',
            color: '#0284c7',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontWeight: '600',
            fontSize: '0.875rem',
            gap: '6px'
          }}
        >
          <FiUsers size={14} />
          {station.employeeCount || 0}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-tags',
      render: station => {
        const isActive = station.status === 'ativo';

        return (
          <div className="manager-tags">
            <span
              className={[
                'manager-tag',
                isActive ? 'manager-tag-success' : 'manager-tag-danger',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: station => {
        const canEdit = permissions.canEdit;
        const canDelete = permissions.canDelete;

        if (!canEdit && !canDelete) return null;

        return (
          <TableActions>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenFiles(station);
              }}
            >
              <FiFolder size={16} />
              Arquivos
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLinkedEmployees(station);
              }}
            >
              <FiUsers size={16} />
              Funcionários Vinculados ({station.employeeCount || 0})
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkEmployees(station);
                }}
              >
                <FiUserPlus size={16} />
                Vincular Funcionários ({station.employeeCount || 0})
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(station);
                }}
              >
                <FiEdit2 size={16} />
                Editar
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="action-danger"
                disabled={user && user.role === 'subuser' && user.access_profile !== 'admin'}
                style={user && user.role === 'subuser' && user.access_profile !== 'admin' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                title={user && user.role === 'subuser' && user.access_profile !== 'admin' ? 'Apenas administradores podem inativar postos' : 'Inativar'}
                onClick={(e) => {
                  e.stopPropagation();
                  if (user && user.role === 'subuser' && user.access_profile !== 'admin') return;
                  handleRequestDelete(station);
                }}
              >
                <FiTrash2 size={16} />
                Inativar
              </button>
            )}
          </TableActions>
        );
      },
    },
  ];

  function ServiceStationManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <ServiceStationManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiMapPin size={22} />
                </div>
                <div>
                  <h1>Postos de Serviço</h1>
                  <p>
                    Visualize, filtre e gerencie todos os postos vinculados à empresa selecionada.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                <Button onClick={() => setIsImportModalOpen(true)} className="manager-header-button" variant="secondary">
                    <FiUpload size={18} />
                    {!isMobile && 'Importar'}
                </Button>
                <Button onClick={handleExport} className="manager-header-button" variant="secondary">
                    <FiDownload size={18} />
                    {!isMobile && 'Exportar'}
                </Button>
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                      <FiPlus size={18} />
                      Posto
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {stations.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredStations.length} de {stations.length} postos
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    label="Empresa"
                    options={companyOptions.map(o => ({
                      value: String(o.value),
                      label: o.label,
                    }))}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="station-search"
                      placeholder="Busque por nome, razão social ou CNPJ"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Status</label>
                  <div className="manager-filter-chips">
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        statusFilter === 'all' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setStatusFilter('all')}
                    >
                      Todos
                    </button>
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        statusFilter === 'ativo' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setStatusFilter('ativo')}
                    >
                      Ativos
                    </button>
                    <button
                      type="button"
                      className={[
                        'manager-chip',
                        statusFilter === 'inativo' && 'manager-chip-active',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setStatusFilter('inativo')}
                    >
                      Inativos
                    </button>
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && stations.length === 0 && (
              <div className="empty-state">
                <p>Nenhum posto de serviço cadastrado.</p>
              </div>
            )}

            {!loading && !error && stations.length > 0 && (
              <Table
                items={paginatedStations}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={station => station.id}
                emptyMessage="Nenhum posto encontrado com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <StationImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={handleFormSuccess}
          companyId={companyFilter !== 'all' ? companyFilter : null}
          companies={companies}
        />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size='xl'
        title={selectedStation ? 'Editar Posto de Serviço' : 'Novo Posto de Serviço'}
        closeOnOverlayClick={false}
      >
        <ServiceStationForm
          companyId={companyFilter !== 'all' ? companyFilter : (company ? company.id : null)}
          initialData={selectedStation}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
          companies={companies}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar inativação"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja inativar o posto de serviço{' '}
            {stationToDelete && (
              <span className="delete-modal-company-name">
                {stationToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            O posto deixará de estar visível para novas operações, mas o histórico será mantido.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Inativando...' : 'Inativar'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title={`Vincular Funcionários - ${selectedStationForLinking?.name}`}
        size="lg"
      >
        <div className="link-modal-content" style={{ padding: '16px', height: '70vh', display: 'flex', flexDirection: 'column' }}>
          {linkLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              Carregando...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', height: '100%' }}>
              
              {/* Seção Vinculados */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ marginBottom: '12px' }}>
                  <h3
                    style={{
                      marginBottom: '8px',
                      fontSize: '1.1rem',
                      color: '#444',
                      marginTop: '0px',
                    }}
                  >
                    Vinculados ({filteredLinkedEmployees.length})
                  </h3>
                  <Input
                    placeholder="Buscar funcionário vinculado..."
                    value={searchLinked}
                    onChange={e => setSearchLinked(e.target.value)}
                    icon={FiSearch}
                  />
                </div>
                
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  {filteredLinkedEmployees.length === 0 ? (
                    <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
                      {searchLinked ? 'Nenhum funcionário encontrado.' : 'Nenhum funcionário vinculado.'}
                    </div>
                  ) : (
                    filteredLinkedEmployees.map(employee => (
                      <div
                        key={employee.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: 'white'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div>
                            <strong>{employee.name}</strong>
                            <span
                              style={{
                                fontSize: '0.85rem',
                                color: '#666',
                                marginLeft: '8px',
                              }}
                            >
                              {employee.cpf}
                            </span>
                          </div>
                          {(() => {
                            const otherStations = employee.serviceStations
                              ? employee.serviceStations.filter(s => s.id !== selectedStationForLinking?.id)
                              : [];
                            
                            if (otherStations.length === 0) return null;

                            const stationList = otherStations.map(s => s.name).join(', ');

                            return (
                              <div style={{ marginTop: '4px' }}>
                                <Tooltip content={stationList}>
                                  <span style={{ 
                                    fontSize: '0.85rem', 
                                    color: '#1565c0', 
                                    cursor: 'pointer',
                                    borderBottom: '1px dotted #1565c0'
                                  }}>
                                    Postos ({otherStations.length})
                                  </span>
                                </Tooltip>
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          onClick={() => handleUnlink(employee.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#e53935',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                          }}
                          className="hover-bg-red-50"
                          title="Desvincular"
                        >
                          <FiUserMinus /> Desvincular
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Seção Não Vinculados */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ marginBottom: '12px' }}>
                   <h3
                    style={{
                      marginBottom: '8px',
                      fontSize: '1.1rem',
                      color: '#444',
                      marginTop: '0px'
                    }}
                  >
                    Não Vinculados ({filteredUnlinkedEmployees.length})
                  </h3>
                  <Input
                    placeholder="Buscar funcionário para vincular..."
                    value={searchUnlinked}
                    onChange={e => setSearchUnlinked(e.target.value)}
                    icon={FiSearch}
                  />
                </div>

                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                     backgroundColor: '#fafafa'
                  }}
                >
                  {filteredUnlinkedEmployees.length === 0 ? (
                    <div style={{ padding: '16px', color: '#888', textAlign: 'center' }}>
                      {searchUnlinked ? 'Nenhum funcionário encontrado.' : 'Todos os funcionários já estão vinculados.'}
                    </div>
                  ) : (
                    filteredUnlinkedEmployees.map(employee => (
                      <div
                        key={employee.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: 'white'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div>
                            <strong>{employee.name}</strong>
                            <span
                              style={{
                                fontSize: '0.85rem',
                                color: '#666',
                                marginLeft: '8px',
                              }}
                            >
                              {employee.cpf}
                            </span>
                          </div>
                          {(() => {
                            const otherStations = employee.serviceStations
                              ? employee.serviceStations.filter(s => s.id !== selectedStationForLinking?.id)
                              : [];
                            
                            if (otherStations.length === 0) return null;

                            const stationList = otherStations.map(s => s.name).join(', ');

                            return (
                              <div style={{ marginTop: '4px' }}>
                                <Tooltip content={stationList}>
                                  <span style={{ 
                                    fontSize: '0.85rem', 
                                    color: '#1565c0', 
                                    cursor: 'pointer',
                                    borderBottom: '1px dotted #1565c0'
                                  }}>
                                    Postos ({otherStations.length})
                                  </span>
                                </Tooltip>
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          onClick={() => handleLink(employee.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#43a047',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                             padding: '4px 8px',
                            borderRadius: '4px',
                          }}
                           className="hover-bg-green-50"
                          title="Vincular"
                        >
                          <FiUserCheck /> Vincular
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
        <div className="delete-modal-actions" style={{ marginTop: '0', padding: '16px', borderTop: '1px solid #eee' }}>
          <Button onClick={() => setIsLinkModalOpen(false)}>Fechar</Button>
        </div>
      </Modal>
      <LinkedEmployeesModal
        isOpen={isLinkedEmployeesModalOpen}
        onClose={() => setIsLinkedEmployeesModalOpen(false)}
        station={selectedStationForLinkedView}
        company={selectedStationForLinkedView?.company || company}
        companies={companies}
      />

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedStationForFiles}
        entityType="station"
        company={selectedStationForFiles?.company || company}
      />
    </PageComponent>
  );
}

